import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./Approvals.css";
import SubHeader from "../Subheader/Subheader";
import OvertimeApproval from "./OvertimApproval/OvertimeApproval";
import DayoffApproval from "./DayoffApproval/DayoffApproval";
import ApprovedReports from "./ApprovedReports/ApprovedReports";
import TimesheetApproval from "./TimesheetApproval/TimesheetApproval";
import SalaryApproval from "./SalaryApproval/SalaryApproval";
import ProjectApproval from "./ProjectApproval/ProjectApproval";
import ProjectApprovalDetail from "./ProjectApproval/ProjectApprovalDetail";
import SketchDesignApproval from "./SketchDesignApproval/SketchDesignApproval";
import PaymentsProgressApproval from "./PaymentsProgressApproval/PaymentsProgressApproval";
import { UserContext } from "../../contexts/CurrentUserContext";
import Badge from "../Report/Badge/Badge";
import { usePendingReports } from "../../contexts/PendingReportsContext";
import { usePendingTimesheets } from "../../contexts/PendingTimesheetsContext";
import { usePendingSalaryApprovals } from "../../contexts/PendingSalaryApprovalsContext";
import { usePendingSketchDesignApprovals } from "../../contexts/PendingSketchDesignApprovalsContext";
import { usePendingPaymentsProgressApprovals } from "../../contexts/PendingPaymentsProgressApprovalsContext";
import {
  hasAccessToOvertimeApproval,
  hasAccessToTimesheetApproval,
  hasAccessToSalaryApproval,
  hasAccessToProjectApproval,
  hasAccessToEpApproval,
} from "../../utils/approvalsAccess";

function Approvals() {
  const { userData } = useContext(UserContext);
  const { pendingOvertimesCount, pendingDayoffsCount, updatePendingCounts } =
    usePendingReports();
  const { pendingTimesheetsCount } = usePendingTimesheets();
  const { pendingSalaryApprovalsCount } = usePendingSalaryApprovals();
  const { pendingSketchDesignApprovalsCount } =
    usePendingSketchDesignApprovals();
  const { pendingPaymentsProgressApprovalsCount } =
    usePendingPaymentsProgressApprovals();

  const canOvertimeApproval = hasAccessToOvertimeApproval(userData);
  const canTimesheetApproval = hasAccessToTimesheetApproval(userData);
  const canSalaryApproval = hasAccessToSalaryApproval(userData);
  const canProjectApproval = hasAccessToProjectApproval(userData);
  const canEpApproval = hasAccessToEpApproval(userData);

  useEffect(() => {
    if (canOvertimeApproval) {
      updatePendingCounts();
    }
  }, [userData, canOvertimeApproval, updatePendingCounts]);

  const tabs = [
    ...(canOvertimeApproval
      ? [
          {
            id: 1,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ПЕРЕРАБОТОК
                <Badge count={pendingOvertimesCount} />
              </div>
            ),
            path: "overtime-approval",
            Component: OvertimeApproval,
          },
          {
            id: 2,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ОТГУЛОВ
                <Badge count={pendingDayoffsCount} />
              </div>
            ),
            path: "dayoff-approval",
            Component: DayoffApproval,
          },
        ]
      : []),
    ...(canTimesheetApproval
      ? [
          {
            id: 3,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ТАБЕЛЕЙ
                {pendingTimesheetsCount > 0 && (
                  <Badge count={pendingTimesheetsCount} />
                )}
              </div>
            ),
            path: "timesheet-approval",
            Component: TimesheetApproval,
          },
        ]
      : []),
    ...(canSalaryApproval
      ? [
          {
            id: 4,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ЗАРПЛАТЫ
                {pendingSalaryApprovalsCount > 0 && (
                  <Badge count={pendingSalaryApprovalsCount} />
                )}
              </div>
            ),
            path: "salary-approval",
            Component: SalaryApproval,
          },
        ]
      : []),
    ...(canOvertimeApproval
      ? [
          {
            id: 5,
            label: "ИСТОРИЯ СОГЛАСОВАНИЙ",
            path: "approved-reports",
            Component: ApprovedReports,
          },
        ]
      : []),
    ...(canProjectApproval
      ? [
          {
            id: 6,
            label: "СОГЛАСОВАНИЕ ПРОЕКТА",
            path: "project-approval",
            Component: ProjectApproval,
          },
        ]
      : []),
    ...(canEpApproval
      ? [
          {
            id: 7,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ПРОЕКТОВ ЭП
                {pendingSketchDesignApprovalsCount > 0 && (
                  <Badge count={pendingSketchDesignApprovalsCount} />
                )}
              </div>
            ),
            path: "sketch-design-approval",
            Component: SketchDesignApproval,
          },
          {
            id: 8,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ВЫПЛАТ ЭП
                {pendingPaymentsProgressApprovalsCount > 0 && (
                  <Badge count={pendingPaymentsProgressApprovalsCount} />
                )}
              </div>
            ),
            path: "payments-progress-approval",
            Component: PaymentsProgressApproval,
          },
        ]
      : []),
  ];

  // Если нет доступа ни к одному согласованию, показываем сообщение
  if (tabs.length === 0) {
    return (
      <div className="approvals">
        <div className="approvals__no-access">
          <h2>Доступ запрещен</h2>
          <p>У вас нет прав для просмотра согласований.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="approvals">
      <SubHeader title="Согласования" tabs={tabs} />
      <Routes>
        {tabs.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
        {canProjectApproval && (
          <Route
            path="project-approval/:projectId/:approvalId"
            element={<ProjectApprovalDetail />}
          />
        )}
        <Route
          path=""
          element={
            <Navigate
              to={tabs.length > 0 ? tabs[0].path : "overtime-approval"}
              replace
            />
          }
        />
      </Routes>
    </div>
  );
}

export default Approvals;
